import { AthenaSiteConfig } from '@/hooks/query/athena/types';
import { UserInfo } from '@nf/types/account';
import { SiteConfigsSettingsData, SiteConfigsSettingsWithSessionData } from '@nf/types/site';

/**
 * 產品顯示條件
 * @param productType 產品類別
 * @param productParam 產品參數 (for casino)
 * @param siteConfig SiteConfig 設定 (from mobile api)
 * @param siteConfigWithSession SiteConfig 設定 (from mobile api with session)
 * @param athenaSiteConfig SiteConfig 設定 (from api-backend api)
 * @param user 用戶資訊 (若有其它附加條件, 如 DCS 或下線開關, 則以 user 內經過 AuthUtil 取得的設定為主)
 * @param allowBeforeLogin 未登入是否無視顯示條件
 */
const canSee = (
	productType: 'exchange' | 'casino',
	productParam: string,
	siteConfig: SiteConfigsSettingsData,
	siteConfigWithSession: SiteConfigsSettingsWithSessionData,
	athenaSiteConfig: AthenaSiteConfig,
	enableESoccerBattle: boolean,
	user: UserInfo | null | undefined,
	allowBeforeLogin?: boolean,
) => {
	const [param1, param2, category, appType] = productParam.split(',');
	const sportId = Number(param1);
	const betTypeId = Number(param2);

	let canSee = false;

	if (!user && allowBeforeLogin) {
		return true;
	}

	if (productType === 'exchange') {
		canSee = user ? Boolean(user.Exchange) : siteConfigWithSession.EnableExchangeForBefore;
	}

	if (productType === 'casino') {
		switch (sportId) {
			case 119: // OFA Live
				canSee = athenaSiteConfig.CanSeeOfaLive;
				break;
			case 124: // Microgaming Live
				canSee = athenaSiteConfig.CanSeeMicrogamingLive;
				break;
			case 129: // Playtech Live
				canSee = athenaSiteConfig.CanSeePTLiveCasino;
				break;
			case 132: // GPI Lottery
				canSee = athenaSiteConfig.CanSeeGPILottery;
				break;
			case 136: // W Casino
				canSee = athenaSiteConfig.CanSeeWLiveCasino;
				break;
			case 138: // ON Casino
				canSee = athenaSiteConfig.CanSeeONLiveCasino;
				break;
			case 144: // GPI Live Casino
				canSee = athenaSiteConfig.CanSeeGPILiveCasino;
				break;
			case 159: // YeeBet
				canSee = athenaSiteConfig.CanSeeYeeBet;
				break;
			case 171:
				switch (appType) {
					case 'b': // Vgaming Mobi
						canSee = user ? user.VGamingMobi : false; // before 固定不顯示
						break;
					default: // Vgaming
						canSee = user ? user.VGaming : false; // before 固定不顯示
						break;
				}
				break;
			case 176: // FGG
				canSee = athenaSiteConfig.CanSeeFggLiveCasino;
				break;
			case 202: // RNG Keno
				canSee = user ? Boolean(user.Keno) : athenaSiteConfig.CanSeeKeno;
				break;
			case 208: // Casino
				canSee = athenaSiteConfig.CanSeeCasino;
				break;
			case 211: // Allbet
				canSee = athenaSiteConfig.CanSeeAllBet;
				break;
			case 212: // Macau
				canSee = athenaSiteConfig.CanSeeRngVB;
				break;
			case 220: // Lottery
				canSee = user ? Boolean(user.KL) : !athenaSiteConfig.CanSeeKenoLottery;
				break;
			case 222: // Table Game
				canSee = user ? Boolean(user.TG) : athenaSiteConfig.CanSeeTableGame;
				break;
			case 228: // WE Live Casino
				canSee = athenaSiteConfig.CanSeeWECasino;
				break;
			case 233: // WM
				canSee = athenaSiteConfig.CanSeeWM;
				break;
			case 237: // Max Game
				canSee = athenaSiteConfig.CanSeeMaxGame;
				break;
			case 239: // BG
				canSee = athenaSiteConfig.CanSeeBG;
				break;
			case 243: // AE Sexy
				canSee = athenaSiteConfig.CanSeeAESexy;
				break;
			case 244: // SA Gaming
				canSee = athenaSiteConfig.CanSeeSAGaming;
				break;
			case 245:
				switch (betTypeId) {
					case 4605: // Sports Mines
						canSee = user ? Boolean(user.SoccerMinesStatus) : false; // before 固定不顯示
						break;
					case 4608: // Sports Towers
						canSee = user ? Boolean(user.SoccerTowersStatus) : false; // before 固定不顯示
						break;
					default: // Virtual Games
						canSee = user ? user.VRG : athenaSiteConfig.CanSeeVirtualGame;
						break;
				}
				break;
			case 248: // Pragmatic Play
				canSee = athenaSiteConfig.CanSeeCasino;
				break;
			case 250: // Asia Gaming
				canSee = athenaSiteConfig.CanSeeAG;
				break;
			case 169:
				switch (betTypeId) {
					case 3616: //Cricket Lottery
						canSee = user ? Boolean(user.CricketLotteryStatus) : true;
						break;
					case 3613: //E-Sports Lottery
						canSee = user ? Boolean(user.ESportsLotteryStatus) : true;
						break;
					case 3614: //NBA Lottery
						canSee = user ? Boolean(user.NBALotteryStatus) : true;
						break;
					default:
						canSee = true;
						break;
				}
				break;
			case 180: // Soccer
				canSee = user ? Boolean(user.VirtuallySoccer) : true;
				break;
			case 112: // Beter
				canSee = enableESoccerBattle;
				break;
			default:
				canSee = true; // 未知的產品一律顯示
				break;
		}
	}

	return canSee;
}

export const productService = {
	canSee
};